// stores/websocket/websocketStore.js
import { defineStore } from 'pinia';
import { useAuthStore } from '../auth';
import { usePlayerStore } from '../player/playerStore';
import { useGamesStore } from '../games/gamesStore';
import { useCategoriesStore } from '../categories/categoriesStore';
import { useProvidersStore } from '../providers/providersStore';
import { useJackpotStore } from '../jackpot/jackpotStore';
import config from '../../config';

import Echo from 'laravel-echo';
import io from 'socket.io-client';

window.io = io;

const createEchoInstance = (token) => {
  return new Echo({
    broadcaster: 'socket.io',
    host: config.wsHost, // Usa la URL de WebSocket correspondiente al entorno
    forceTLS: config.wsConfig.forceTLS, // Se adapta al entorno
    encrypted: config.wsConfig.encrypted, // Se adapta al entorno
    auth: {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json'
      }
    },
    transports: ['websocket', 'polling'],
    authEndpoint: config.endpoints.broadcasting // Endpoint dinámico para autenticación

  });
};

export const useWebSocketStore = defineStore('websocket', {
  state: () => ({
    echo: null,
    isConnected: false,
    lastBalanceUpdate: Date.now(), // AGREGAR ESTO
    inactivityTimer: null // AGREGAR ESTO
  }),

  actions: {
    async initialize() {
      const authStore = useAuthStore();
      if (!authStore.token || !authStore.user) {
        console.warn('⚠️ Cannot initialize WebSocket without auth token');
        return false;
      }
    
      // Desconectar instancia anterior si existe
      if (this.echo) {
        this.disconnect();
      }
    
      // Crear nueva instancia
      this.setupWebSocket(authStore.token, authStore.user);
      
      // Esperar a que se establezca la conexión (hasta 5 segundos)
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 10;
        
        const checkConnection = () => {
          attempts++;
          console.log(`🔄 Verificando conexión WebSocket (intento ${attempts}/${maxAttempts})...`);
          
          if (this.isConnected) {
            console.log('✅ WebSocket conectado exitosamente');
            resolve(true);
            return;
          }
          
          if (attempts >= maxAttempts) {
            console.warn('⚠️ No se pudo establecer conexión WebSocket después de múltiples intentos');
            resolve(false);
            return;
          }
          
          setTimeout(checkConnection, 500);
        };
        
        // Iniciar verificación
        setTimeout(checkConnection, 500);
      });
    },

    setupWebSocket(token, user) {
      if (!this.echo) {
        console.log('📊 Initial WebSocket State:', {
          userId: user.id,
          hasToken: !!token
        });

        this.echo = createEchoInstance(token);
        this.setupSocketListeners();
        this.subscribeToChannel(user.id);
      }
    },

    setupSocketListeners() {
      if (!this.echo) return;
    
      this.echo.connector.socket
        .on('connect', () => {
          console.log('🔌 Connection established', {
            socketId: this.echo.socketId(),
            transport: this.echo.connector.socket.io.engine.transport.name
          });
          this.isConnected = true;
        })
        .on('connect_error', (error) => {
          console.error('🔴 Connection error:', {
            message: error.message,
            description: error.description,
            type: error.type
          });
        })
        .on('connect_timeout', () => {
          console.error('🔴 Connection timeout');
        })
        .on('disconnect', (reason) => {
          console.log('🔌 Disconnection detected', {
            reason: reason
          });
          this.isConnected = false;
        })
        .on('reconnecting', (attemptNumber) => {
          console.log('🔄 Trying to reconnect...', {
            attempt: attemptNumber
          });
        })
        .on('reconnect', (attemptNumber) => {
          console.log('✅ Successful reconnection', {
            attempt: attemptNumber
          });
          this.isConnected = true;
        })
        .on('reconnect_error', (error) => {
          console.error('🔴 Reconnection error:', {
            message: error.message
          });
        })
        .on('reconnect_failed', () => {
          console.error('🔴 Reconnection failed after all attempts');
        })
        .on('error', (error) => {
          console.error('❌ Error in socket:', error);
        });
        
      // Verificar configuración
      console.log('🔧 Echo configuration:', {
        broadcaster: this.echo.broadcaster,
        host: this.echo.options.host,
        authEndpoint: this.echo.options.authEndpoint,
        transports: this.echo.connector.socket.io.opts.transports
      });
    },
    subscribeToChannel(userId) {
      if (!this.echo) {
        console.warn('⚠️ No Echo instance to subscribe');
        return;
      }

      const channelName = `player.${userId}`;
      console.log('📡 Trying to connect to channel:', channelName);

      try {
        const channel = this.echo.private(channelName);
        const playerStore = usePlayerStore();
        const gamesStore = useGamesStore();
        const categoriesStore = useCategoriesStore();
        const providersStore = useProvidersStore();

        channel.subscribed(() => {
          console.log('✅ Subscribed to channel success:', {
            channel: channelName,
            socketId: this.echo.socketId()
          });
        });

        // Balance updates
        // Balance updates
// En websocketStore.js, asegúrate de que el listener se vea así:
channel.listen('.BalanceUpdated', (event) => {
   console.log('📡 Balance actualizado - reseteando timer');
        this.lastBalanceUpdate = Date.now(); // AGREGAR
        this.checkInactivity(); // AGREGAR
  console.log('📡 Received BalanceUpdated event:', {
    new_balance: event.new_balance,
    old_balance: event.old_balance,
    bonus_balance: event.bonus_balance,
    notification: event.notification
  });
  
  // Llamar a updateBalance con todos los parámetros
  playerStore.updateBalance(
    event.new_balance, 
    event.old_balance, 
    event.bonus_balance,
    event.notification
  );
});
        // Añadir escucha de favoritos
        channel.listen('.favorites.updated', (event) => {
          gamesStore.updateFavoritesFromEvent(event.favorites);
        });
  
        // Games updates
        channel.listen('.GamesUpdated', (event) => {
          gamesStore.updateGamesFromSocket(event.games);
        });

        // Categories updates
        channel.listen('.CategoryUpdated', (event) => {
          categoriesStore.updateCategories(event.categories);
        });

        // Providers updates
        channel.listen('.ProvidersUpdated', (event) => {
          providersStore.updateProviders(event.providers);
        });

        // Jackpots updates
        channel.listen('.JackpotUpdated', (event) => {
          console.log('📡 Recibido evento JackpotUpdated:', event);
          const jackpotStore = useJackpotStore();
          if (event.jackpots) {
            jackpotStore.updateJackpotsFromSocket(event.jackpots);
          } else {
            console.warn('⚠️ Evento JackpotUpdated sin datos de jackpots:', event);
          }
        });
        
        

        channel.error((error) => {
          console.error('❌ Error en canal:', {
            error,
            canal: channelName
          });
        });

      } catch (error) {
        console.error('❌ Error creating channel:', error);
      }
    },
// NUEVO MÉTODO - Agregar después de subscribeToChannel
// MODIFICAR el método checkInactivity:
checkInactivity() {
  const gamesStore = useGamesStore();
  
  // Limpiar timer anterior
  if (this.inactivityTimer) {
    clearInterval(this.inactivityTimer);
  }
  
  // Solo si hay juego activo
  if (gamesStore.activeGame) {
    console.log('⏰ Iniciando verificación de inactividad');
    
    // AGREGAR ESTO - Resetear el tiempo al abrir juego nuevo
    this.lastBalanceUpdate = Date.now();
    console.log('🔄 Timer reseteado para nuevo juego');
    
    // Verificar cada minuto
    this.inactivityTimer = setInterval(() => {
      const now = Date.now();
      const timeSinceLastUpdate = now - this.lastBalanceUpdate;
      
      console.log('🕐 Tiempo sin actualización:', Math.floor(timeSinceLastUpdate / 1000), 'segundos');
      
      // Si hay juego Y pasaron 8 minutos sin balance update
      if (gamesStore.activeGame && timeSinceLastUpdate > 8 * 60 * 1000) {
        console.log('❌ 8 minutos sin actividad - cerrando juego');
        
        // Mostrar mensaje temporal (3 segundos)
        const message = document.createElement('div');
        message.innerHTML = 'Sesión inactiva. Regresando al lobby...';
        message.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:#000;color:#fff;padding:20px;border-radius:10px;z-index:9999';
        document.body.appendChild(message);
        
        gamesStore.closeGame();
        
        // Quitar mensaje después de 3 segundos
        setTimeout(() => {
          if (message.parentNode) {
            message.remove();
          }
        }, 3000);
        
        // Limpiar timer
        clearInterval(this.inactivityTimer);
        this.inactivityTimer = null;
      }
    }, 60000); // Verificar cada minuto
  }
},
    disconnect() {
       // AGREGAR limpieza del timer
      if (this.inactivityTimer) {
        clearInterval(this.inactivityTimer);
        this.inactivityTimer = null;
      }
      if (this.echo) {
        this.echo.disconnect();
        this.echo = null;
        this.isConnected = false;
      }
    }
  }
});
